import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { SessionCondition } from 'app/audience/components/session-condition-group.model';
import SessionConditionHelpers from 'app/audience/session-condition-helpers';
import { WidgetSimpleInfo } from 'app/campaign/widgets.model';

export interface SavedSessionCondition {
  name?: string;
}

interface TitleText {
  key: string;
  params: Record<string, number>;
}

interface ConditionItem {
  text: string;
  icon?: string;
  svgIcon?: string;
}

@Component({
  selector: 'app-session-conditions-trigger',
  templateUrl: './session-conditions-trigger.component.html',
  styleUrls: ['./session-conditions-trigger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SessionConditionsTriggerComponent {
  private readonly translateService = inject(TranslateService);

  readonly conditions = input<SessionCondition[] | undefined>([]);
  readonly savedCondition = input<SavedSessionCondition | undefined>();
  /** Undefined until loaded. */
  readonly widgets = input<WidgetSimpleInfo[] | undefined>();
  readonly unavailableReasonKey = input<string | null | undefined>();
  readonly opened = output<void>();

  private readonly language = toSignal(this.translateService.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translateService.currentLang,
  });

  protected readonly items = computed<ConditionItem[]>(() => {
    // Re-translates on a language switch.
    this.language();
    const widgets = this.widgets();
    const saved = this.savedCondition();
    const conditions = (this.conditions() ?? []).map((condition) => this.describe(condition, widgets));
    if (!saved) {
      return conditions;
    }
    const label = this.translateService.instant('audience.settings.saved-condition');
    return [{ text: saved.name ? `${label} = ${saved.name}` : label, icon: 'save' }, ...conditions];
  });

  protected readonly title = computed<TitleText>(() => {
    const count = (this.conditions()?.length ?? 0) + (this.savedCondition() ? 1 : 0);
    if (count === 0) {
      return { key: 'campaign.widget.all-sessions', params: {} };
    }
    return count === 1
      ? { key: 'campaign.widget.session-condition-count-one', params: {} }
      : { key: 'campaign.widget.session-condition-count', params: { count } };
  });

  protected readonly emptyDetailKey = computed<string>(
    () => this.unavailableReasonKey() ?? 'campaign.widget.all-sessions-hint',
  );

  protected readonly tooltip = computed<string>(() => {
    const texts = this.items().map((item) => item.text);
    const reasonKey = this.unavailableReasonKey();
    // Without conditions the detail line already shows the reason.
    return reasonKey && texts.length ? this.translateService.instant(reasonKey) : texts.join('\n');
  });

  protected open(event: Event): void {
    event.preventDefault();
    if (event instanceof KeyboardEvent && event.repeat) {
      return;
    }
    if (!this.unavailableReasonKey()) {
      this.opened.emit();
    }
  }

  private describe(condition: SessionCondition, widgets: WidgetSimpleInfo[] | undefined): ConditionItem {
    const skipValue = !widgets && SessionConditionHelpers.CampaignSessionConditionTypes.includes(condition.type);
    const config = SessionConditionHelpers.getSessionConditionConfig(condition.type);
    const translated = SessionConditionHelpers.translateSessionCondition(
      { ...condition, conditionConfig: config, conditionTranslations: {} },
      skipValue,
      this.translateService,
      widgets ?? [],
    ).conditionTranslations;
    const text = SessionConditionHelpers.describeTranslatedCondition(
      skipValue ? { ...translated, operator: undefined } : translated,
      this.translateService,
    );
    return { text, icon: config?.icon, svgIcon: config?.svgIcon };
  }
}
